import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Map, Download, RefreshCw, ArrowRight, Plane, Lock, Sparkles, Share2, Copy, Check, X, Bookmark, ChevronDown, Navigation } from "lucide-react";
import axios from "axios";
import { API, placeImg } from "../utils/constants";
import { formatDuration } from "../utils/smartDuration";
import BrandHeader from "../components/BrandHeader";
import PaymentModal from "./PaymentModal";
import { tipItems, groupedTips, tipText, beforeYouGo, LABELS } from "../utils/tips";
import { FanMark } from "../components/FanMark";
import { trackStep } from "../utils/funnel";

// ── Editorial design tokens (the partner's design handoff, screen-native
// expression). The obsidian surfaces are theme-fixed by design; everything
// else rides the zen CSS variables so dark mode keeps working.
const RED = "#E82529";
const INK = "#1E1A15";
const WASHI = "#F5F5F0";
const HAIR_D = "rgba(240,235,224,.09)";
const BODY_D = "rgba(240,235,224,.72)";
const MUTED_D = "rgba(240,235,224,.48)";

const MONO = '"IBM Plex Mono", monospace';
const DISPLAY = '"Unbounded", sans-serif';
const SANS = '"DM Sans", system-ui, sans-serif';

// Sharing leaves the app: preserve the serving origin and configured API path.
const publicShareUrl = (token, api, pageUrl) =>
  new URL(`${api.replace(/\/$/, "")}/shared/${encodeURIComponent(token)}`, pageUrl).href;

const badgeLabel = (cat) => {
  const c = (cat || "").toUpperCase().trim();
  return { HIDDEN: "HIDDEN GEM" }[c] || c || "SPOT";
};

const fmtDocDate = (dateStr) => {
  // '2026-09-01' → '01 SEP 2026' — the document's mono date style.
  const m = /(\d{4})-(\d{2})-(\d{2})/.exec(dateStr || "");
  if (!m) return dateStr || "";
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${m[3]} ${months[parseInt(m[2], 10) - 1]} ${m[1]}`;
};

const Eyebrow = ({ children, color = RED }) => (
  <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color }}>{children}</div>
);

const CatBadge = ({ cat }) => (
  <span style={{ border: "1px solid var(--zen-text)", borderRadius: 999, padding: "2px 8px", fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: ".14em", color: "var(--zen-text)", whiteSpace: "nowrap" }}>
    {badgeLabel(cat)}
  </span>
);

const ResultPage = ({ tripConfig, navigateTo, startOver, onSaveTrip, hasCompanion, userEmail, setUserEmail, setTripConfig, sessionToken }) => {
  const itinerary = tripConfig?.itinerary;
  const isLocked = !!itinerary?.locked;
  useEffect(() => { if (isLocked) trackStep("preview_locked"); }, [isLocked]);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState("");
  // The layers: which stop rows are unfolded to full detail.
  const [openStops, setOpenStops] = useState(() => new Set());
  const shareModalRef = useRef(null);

  const flashError = (msg) => { setActionError(msg); setTimeout(() => setActionError(""), 5000); };

  const toggleStop = (key) => setOpenStops(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Rare path: someone with existing credits (signed in) lands on a teaser.
  const tryUnlockWithCredits = async (email, credits) => {
    try {
      const res = await axios.post(`${API}/itinerary/${itinerary.id}/unlock`, {},
        sessionToken ? { headers: { Authorization: `Bearer ${sessionToken}` } } : {});
      setTripConfig?.(prev => ({ ...(prev || {}), itinerary: res.data }));
      setShowUnlockModal(false);
    } catch (e) {
      setShowUnlockModal(false);
      flashError("You have credits — sign in from My Trips first, then unlock from there.");
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareModalRef.current && !shareModalRef.current.contains(e.target)) {
        setShowShareModal(false);
      }
    };
    if (showShareModal) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShareModal]);

  if (!tripConfig || !itinerary || !itinerary.days || itinerary.days.length === 0) return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--zen-bg)" }}>
      <BrandHeader />
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center space-y-4">
          <p className="text-sm" style={{ color: "var(--zen-sub)" }}>
            {itinerary ? "Your itinerary appears to be empty." : "No itinerary found."}
          </p>
          <button onClick={() => navigateTo("summary")} className="zen-btn zen-btn-primary px-6 py-2.5 text-sm">
            Go back to your plan
          </button>
        </div>
      </div>
    </div>
  );

  const downloadPdf = async () => {
    setIsDownloading(true);
    try {
      const res = await fetch(`${API}/itinerary/${itinerary.id}/pdf`);
      if (!res.ok) throw new Error(`PDF request failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Your_Japan_Trip_Unfolded.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download error:", e);
      flashError("Couldn't create your PDF — please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const res = await axios.post(`${API}/itinerary/${itinerary.id}/share`);
      const token = res.data.share_token;
      const link = publicShareUrl(token, API, window.location.href);
      setShareLink(link);
      setShowShareModal(true);
    } catch (e) {
      console.error("Share error:", e);
      flashError("Couldn't create a share link — please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    const text = `Check out my Japan trip itinerary! ${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  // Display the same dated travel windows the planner used. Missing legacy
  // logistics stay unspecified instead of inventing flight times or buffers.
  const travelEvents = (itinerary.calendar_frame?.days || []).flatMap(d => d.events || []);
  const arrivalEvent = travelEvents.find(e => e.kind === "arrival");
  const departureEvent = travelEvents.find(e => e.kind === "departure");
  const arrivalTime = arrivalEvent?.start || itinerary.arrival_time || itinerary.profiler_brief?.arrival_time || tripConfig.arrival_time;
  const departureTime = departureEvent?.end || itinerary.departure_time || itinerary.profiler_brief?.departure_time || tripConfig.departure_time;
  const fmtClock = value => {
    if (!value) return "";
    const [h, m] = value.split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  };
  const totalDays = itinerary.days?.length || 0;
  const cities = itinerary.cities || [];
  const spotCount = (itinerary.days || []).reduce((sum, d) => sum + (d?.time_blocks || []).reduce((s, b) => {
    const isTravel = b?.time_of_day === "travel" || b?.cluster_name?.toLowerCase() === "travel";
    return s + (isTravel ? 0 : (b?.locations || []).filter(l => l && l.category !== "travel" && !l.is_optional).length);
  }, 0), 0);
  // The brand cover (hi-res) — catalog photos are 600-800px and go soft
  // when stretched full-bleed; the obsidian scrim keeps it a backdrop.
  const heroPhoto = "/cover-gion.jpg";
  const year = (itinerary.start_date || "").slice(0, 4) || new Date().getFullYear();

  const TransitBadge = ({ transit }) => {
    if (!transit || !transit.minutes) return null;
    const isWalk = (transit.method || "").toLowerCase() === "walk";
    const stations = !isWalk && transit.from_station && transit.to_station
      ? `${transit.from_station.replace(/ Station$/i, " Stn")} → ${transit.to_station.replace(/ Station$/i, " Stn")}`
      : null;
    const via = !isWalk && transit.line ? transit.line : (transit.method || "transit");
    const steps = (transit.steps || []).filter(Boolean);
    if (steps.length) {
      // the way, said plainly (core/local_transit.leg_steps) — walk how long, which line from where to where,
      // where to change — and the live route in Maps (owner, 2026-10-21)
      const head = isWalk ? "WALK" : transit.line ? "METRO / TRAIN" : (transit.method || "").includes("taxi") ? "BUS OR TAXI" : "GETTING THERE";
      return (
        <div className="my-2 px-3 py-2" data-testid="transit-badge"
             style={{ borderLeft: `2px solid ${RED}`, background: "var(--zen-wash, #FAF7F0)" }}>
          <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 2 }}>
            <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", color: RED }}>
              → {head} · {transit.minutes} MIN
            </div>
            {transit.maps_url && (
              <a href={transit.maps_url} target="_blank" rel="noopener noreferrer"
                 style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: ".12em", color: RED, textDecoration: "none" }}>
                ROUTE IN MAPS ↗
              </a>
            )}
          </div>
          {steps.map((line, i) => (
            <div key={i} style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 12.5, lineHeight: 1.45, color: "var(--zen-ink, #0D0D0D)" }}>
              {line}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="py-2" data-testid="transit-badge">
        <div className="flex items-center gap-3">
          <div className="flex-1" style={{ borderTop: "1px solid var(--zen-border)" }} />
          <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-dim)", textAlign: "center" }}>
            <span style={{ color: RED }}>→</span> {transit.minutes} MIN · {via.toUpperCase()}
            {stations && <span style={{ color: "var(--zen-sub)" }}> · {stations.toUpperCase()}</span>}
          </div>
          <div className="flex-1" style={{ borderTop: "1px solid var(--zen-border)" }} />
        </div>
        {transit.hint && (
          <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 12, color: "var(--zen-sub)", textAlign: "center", marginTop: 4 }}>
            ↳ {transit.hint}
          </div>
        )}
      </div>
    );
  };

  // ── The stop row — the document's core component, made tappable.
  // Collapsed: meta · title+badge · hook · first tip. Unfolded: every tip,
  // the insider note, best hours, address, and a maps button — the layers.
  const StopRow = ({ loc, tod, dayCity, rowKey }) => {
    const open = openStops.has(rowKey);
    const items = tipItems(loc);          // gated + priority-ordered (core/tips.py)
    // the dinner worth booking shows how to book first (owner, 2026-10-21)
    const top = (loc.meal_role === "dinner_premium" && items.find(i => i.type === "BOOK")) || items[0];
    const hasMore = items.length > 1 || loc.insider_note || loc.address || loc.best_hours || loc.open_hours;
    const mapsUrl = loc.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((loc.name || "") + ", " + (loc.address || loc.city || dayCity || "Japan"))}`;
    return (
      <div style={{ border: "1px solid var(--zen-border)", borderRadius: 4, overflow: "hidden", background: "var(--zen-s1)" }}>
        <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[136px_1fr]" style={{ gap: 14, cursor: hasMore ? "pointer" : "default" }}
          onClick={() => hasMore && toggleStop(rowKey)} data-testid={`stop-row-${loc.name}`}>
          <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 96, overflow: "hidden", background: loc.is_supplementary && !loc.image ? INK : "var(--zen-s2)" }}>
            {loc.image && (
              <img src={placeImg(loc.image)} alt={loc.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                onError={(e) => { e.target.style.visibility = "hidden"; }} />
            )}
            {loc.is_supplementary && !loc.image && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ padding: 8 }}>
                <FanMark size={18} toriiColor={WASHI} title="" style={{ marginBottom: 6 }} />
                <span style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 10, lineHeight: 1.2, color: WASHI }}>{loc.name}</span>
              </div>
            )}
            {loc.is_supplementary && (
              <span className="absolute" style={{ left: 6, top: 6, background: RED, color: WASHI, fontFamily: MONO, fontWeight: 600, fontSize: 7.5, letterSpacing: ".14em", padding: "3px 6px", borderRadius: 2 }} data-testid="your-pick-badge">★ YOUR PICK</span>
            )}
          </div>
          <div style={{ padding: "8px 14px 8px 0", minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div className="flex items-baseline justify-between gap-2" style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-dim)" }}>
              <span className="truncate">{(tod || "").replace("_", " ")}{loc.neighborhood ? ` · ${loc.neighborhood}` : ""}</span>
              {loc.visit_duration_hours && <span className="shrink-0">~{formatDuration(loc.visit_duration_hours)}</span>}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap" style={{ marginTop: 5 }}>
              <h4 style={{ fontFamily: SANS, fontWeight: 800, fontSize: 17, letterSpacing: "-.01em", color: "var(--zen-text)", margin: 0, lineHeight: 1.2 }}>{loc.name || "Unknown spot"}</h4>
              <CatBadge cat={loc.category} />
              {loc.meal_role && (
                <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 8.5, letterSpacing: ".14em", textTransform: "uppercase", color: RED, border: `1px solid ${RED}`, padding: "2px 6px", borderRadius: 2 }} data-testid="meal-chip">{loc.meal_role === "dinner_premium" ? "dinner · to book" : loc.meal_role}{loc.is_optional ? " · optional" : ""}</span>
              )}
            </div>
            {loc.from_here && (
              <p style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--zen-dim)", margin: "5px 0 0" }} data-testid="meal-distance">{loc.from_here} from where you are</p>
            )}
            {loc.attribution && loc.map_url && (
              <a href={loc.map_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                style={{ display: "inline-block", fontSize: 12.5, fontWeight: 600, color: "var(--zen-text)", textDecoration: "underline", textUnderlineOffset: 3, margin: "6px 0 0" }}
                data-testid="meal-google-link">Today's hours and reviews on {loc.attribution} ↗</a>
            )}
            {loc.description && <p style={{ fontFamily: SANS, fontSize: 13.5, lineHeight: 1.42, color: "var(--zen-sub)", margin: "6px 0 0" }}>{loc.description}</p>}
            {top && (
              <p style={{ fontSize: 13, lineHeight: 1.4, margin: "6px 0 0", borderLeft: `2px solid ${RED}`, paddingLeft: 11, color: "var(--zen-sub)" }}>
                <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 8.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-text)", marginRight: 6 }}>{LABELS[top.type]}</span>
                {tipText(top)}
              </p>
            )}
            {hasMore && (
              <div className="flex items-center gap-1" style={{ marginTop: 7, fontFamily: MONO, fontWeight: 600, fontSize: 9, letterSpacing: ".14em", color: RED }}>
                <ChevronDown className="w-3 h-3" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                {open ? "LESS" : "MORE"}
              </div>
            )}
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} style={{ overflow: "hidden" }}>
              <div style={{ borderTop: "1px solid var(--zen-border)", padding: "12px 14px", background: "var(--zen-s2)" }}>
                {/* Every tip, grouped by the job it does — one calm voice, in priority order */}
                {groupedTips(loc).map(g => ({ ...g, items: g.items.filter(it => it.text !== top?.text) })).filter(g => g.items.length).map((g, gi) => (
                  <div key={g.type} style={{ marginTop: gi === 0 ? 0 : 9 }}>
                    <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 8.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-dim)", marginBottom: 3 }}>{g.label}</div>
                    {g.items.map((it, ti) => (
                      <p key={ti} style={{ fontSize: 13, lineHeight: 1.45, borderLeft: `2px solid ${RED}`, paddingLeft: 11, color: "var(--zen-sub)", margin: ti === 0 ? 0 : "5px 0 0" }}>{tipText(it)}</p>
                    ))}
                  </div>
                ))}
                {loc.insider_note && (
                  <p style={{ fontSize: 13, fontStyle: "italic", color: "var(--zen-sub)", marginTop: items.length > 1 ? 9 : 0 }} data-testid="insider-note">☆ {loc.insider_note}</p>
                )}
                <div className="flex items-center flex-wrap" style={{ gap: "6px 16px", marginTop: 10 }}>
                  {loc.open_hours && loc.open_hours !== loc.best_hours && <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".12em", color: "var(--zen-dim)" }}>{loc.open_hours_date ? 'OPEN THIS DAY' : 'OPEN'} {loc.open_hours}</span>}
                  {loc.best_hours && <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".12em", color: "var(--zen-dim)" }}>BEST {loc.best_hours}</span>}
                  {loc.name_ja && <span style={{ fontSize: 12, color: "var(--zen-dim)" }}>{loc.name_ja}</span>}
                  {loc.cash_only && <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".12em", color: "var(--zen-dim)" }}>CASH ONLY</span>}
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 hover:opacity-70" style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".12em", color: RED }}
                    data-testid="place-address-link">
                    <Navigation className="w-3 h-3" /> TAKE ME THERE
                  </a>
                </div>
                {loc.address && <p style={{ fontSize: 11, color: "var(--zen-dim)", marginTop: 5 }}>{loc.address}</p>}
                {loc.is_supplementary && loc.reputation_signal && <p style={{ fontSize: 12, fontStyle: "italic", color: "var(--zen-sub)", marginTop: 6 }}>{loc.reputation_signal}</p>}
                {loc.image_source === "wikimedia" && loc.image_credit && <p style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: ".06em", color: "var(--zen-dim)", marginTop: 6 }}>PHOTO: {loc.image_credit}</p>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Travel as an interstitial — deliberately photo-less, like the document.
  const TravelRow = ({ block, day }) => {
    const travelLoc = block.locations?.[0];
    const travelDur = travelLoc?.visit_duration_hours;
    const durLabel = travelDur ? `~${formatDuration(travelDur).toUpperCase()}` : "";
    const title = day.city_label || travelLoc?.name || `Traveling to ${day.city}`;
    const service = (travelLoc?.description || "").trim();
    const tip = (travelLoc?.tips || []).filter(Boolean)[0] || "";
    const j = travelLoc?.journey;
    if (j) {
      // the change of city as instructions (owner, 2026-10-21): the verified guide of that train
      const mins = j.minutes || 0;
      const fare = j.fare_jpy || {};
      const fareText = [fare.reserved && `¥${Number(fare.reserved).toLocaleString()} reserved seat`,
                        fare.unreserved && `¥${Number(fare.unreserved).toLocaleString()} unreserved`].filter(Boolean).join(" · ");
      const Row = ({ label, text }) => text ? (
        <div className="grid gap-3 py-1" style={{ gridTemplateColumns: "72px 1fr", borderTop: "1px solid var(--zen-border)" }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: ".14em", color: "var(--zen-dim)", paddingTop: 2 }}>{label}</div>
          <div style={{ fontFamily: SANS, fontSize: 13, lineHeight: 1.45, color: "var(--zen-text)" }}>{text}</div>
        </div>
      ) : null;
      return (
        <div style={{ border: "1px solid var(--zen-border)", borderLeft: `3px solid ${RED}`, background: "var(--zen-s2)", padding: "12px 14px", borderRadius: 4 }} data-testid="travel-block">
          <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", color: RED }}>
            CHANGING CITY · {(j.from || "").toUpperCase()} → {(j.to || "").toUpperCase()}{mins ? ` · ABOUT ${Math.floor(mins / 60)}H ${String(mins % 60).padStart(2, "0")}M ON THE TRAIN` : ""}
          </div>
          <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 16, margin: "6px 0 6px", color: "var(--zen-text)" }}>{j.best_way}</div>
          <Row label="BOARD" text={j.board_at} />
          <Row label="ARRIVE" text={j.arrive_at} />
          <Row label="FARE" text={fareText ? `${fareText} (one adult, one way).` : ""} />
          <Row label="BOOK" text={j.book} />
          <Row label="BAGS" text={j.luggage} />
          {(j.steps || []).length > 0 && (
            <>
              <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: ".14em", color: "var(--zen-dim)", marginTop: 8 }}>STEP BY STEP</div>
              <ol style={{ margin: "4px 0 0 18px", fontFamily: SANS, fontSize: 13, lineHeight: 1.45, color: "var(--zen-text)", listStyle: "decimal" }}>
                {j.steps.slice(0, 6).map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </>
          )}
          {(j.tips || []).length > 0 && (
            <>
              <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: ".14em", color: "var(--zen-dim)", marginTop: 8 }}>GOOD TO KNOW</div>
              <ul style={{ margin: "4px 0 0 18px", fontFamily: SANS, fontSize: 13, lineHeight: 1.45, color: "var(--zen-text)", listStyle: "disc" }}>
                {j.tips.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </>
          )}
        </div>
      );
    }
    return (
      <div style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "12px 14px" }} data-testid="travel-block">
        <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-dim)" }}>
          IN TRANSIT{service ? ` · ${service}` : ""}{durLabel ? ` · ${durLabel}` : ""}
        </div>
        <div className="flex items-baseline gap-2 flex-wrap" style={{ marginTop: 6 }}>
          <h4 style={{ fontFamily: SANS, fontWeight: 800, fontSize: 17, letterSpacing: "-.01em", color: "var(--zen-text)", margin: 0 }}>{title}</h4>
          <span style={{ border: "1px solid var(--zen-text)", borderRadius: 999, padding: "2px 8px", fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: ".14em", color: "var(--zen-text)" }}>TRAVEL</span>
        </div>
        {tip && <p style={{ fontSize: 13, lineHeight: 1.4, margin: "8px 0 0", borderLeft: `2px solid ${RED}`, paddingLeft: 11, color: "var(--zen-sub)" }}>{tip}</p>}
      </div>
    );
  };

  const monoBtn = { fontFamily: SANS, fontWeight: 700, fontSize: 13, cursor: "pointer" };

  return (
    <div className="min-h-screen" style={{ background: "var(--zen-bg)" }}>
      <BrandHeader right={
        <div className="flex items-center gap-4">
          <button onClick={() => navigateTo("summary")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:opacity-80"
            style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", color: "var(--zen-text)", cursor: "pointer" }}
            data-testid="back-to-plan-btn">
            ← Back to my plan
          </button>
          <button onClick={startOver}
            className="font-mono-label flex items-center gap-1.5 hover:opacity-70 transition-opacity"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, letterSpacing: "0.16em", color: "var(--zen-dim)" }}
            data-testid="start-over-btn">
            <RefreshCw className="w-3 h-3" /> Start over
          </button>
        </div>
      } />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* ── OBSIDIAN HERO — the document's cover, made for a screen ── */}
        <div className="relative overflow-hidden" style={{ background: INK, borderRadius: 8 }}>
          {heroPhoto && (
            <img src={heroPhoto} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "60% 40%" }} />
          )}
          <div style={{ position: "absolute", inset: 0, background: INK, opacity: 0.78 }} />
          <div className="relative" style={{ padding: "clamp(24px, 5vw, 44px)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <FanMark size={22} toriiColor="#F5F5F0" title="" />
                <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 10, letterSpacing: ".18em", color: MUTED_D }}>JAPANUNFOLDED.AI</span>
              </div>
              <span className="hidden sm:block" style={{ fontFamily: MONO, fontWeight: 600, fontSize: 10, letterSpacing: ".18em", color: MUTED_D }}>ITINERARY · {year}</span>
            </div>

            <div style={{ width: 56, height: 4, background: RED, margin: "clamp(20px, 4vw, 36px) 0 18px" }} />
            <h1 style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: "clamp(26px, 5.5vw, 52px)", lineHeight: 1.02, letterSpacing: "-.02em", color: WASHI, margin: 0, textWrap: "balance" }} data-testid="trip-title">
              {itinerary.title && itinerary.title !== "Your Japan Trip"
                ? itinerary.title
                : <>JAPAN UN·<span style={{ color: RED }}>FOLDED</span></>}
            </h1>
            <div className="flex items-center flex-wrap" style={{ gap: "4px 10px", marginTop: 16, fontFamily: SANS, fontWeight: 700, fontSize: 15, color: "#F0EBE0" }}>
              {cities.map((c, i) => (
                <span key={c} className="flex items-center gap-2.5">
                  {i > 0 && <span style={{ color: RED }}>→</span>}{c}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-3" style={{ gap: 16, borderTop: `1px solid ${HAIR_D}`, borderBottom: `1px solid ${HAIR_D}`, padding: "18px 0", marginTop: 22 }}>
              {[[itinerary.total_days || totalDays, "DAYS"], [cities.length, cities.length === 1 ? "CITY" : "CITIES"], [spotCount, "PLACES"]].map(([n, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: "clamp(26px, 4vw, 38px)", lineHeight: 1, color: WASHI }}>{n}</div>
                  <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 10, letterSpacing: ".16em", color: MUTED_D, marginTop: 6 }}>{l}</div>
                </div>
              ))}
            </div>

            <div className="relative flex flex-wrap items-center gap-2" style={{ marginTop: 20 }}>
              {isLocked && (
                <button onClick={() => setShowUnlockModal(true)}
                  className="inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
                  style={{ ...monoBtn, padding: "12px 24px", borderRadius: 2, background: RED, border: "none", color: "#fff", fontSize: 14 }}
                  data-testid="unlock-trip-btn">
                  <Lock className="w-4 h-4" /> Unfold my full trip
                </button>
              )}
              {!isLocked && <>
                <button onClick={() => { try { onSaveTrip?.(); } catch (e) { console.error("Save trip error:", e); } }} data-testid="save-trip-btn"
                  className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                  style={{ ...monoBtn, fontWeight: 600, padding: "10px 16px", borderRadius: 2, border: `1px solid ${BODY_D}`, background: "transparent", color: WASHI }}>
                  <Bookmark className="w-4 h-4" /> Save trip
                </button>
                <button onClick={downloadPdf} disabled={isDownloading} data-testid="export-pdf-btn"
                  className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                  style={{ ...monoBtn, fontWeight: 600, padding: "10px 16px", borderRadius: 2, border: `1px solid ${BODY_D}`, background: "transparent", color: WASHI }}>
                  {isDownloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
                </button>
                <button onClick={handleShare} disabled={isSharing} data-testid="share-itinerary-btn"
                  className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                  style={{ ...monoBtn, fontWeight: 600, padding: "10px 16px", borderRadius: 2, border: `1px solid ${BODY_D}`, background: "transparent", color: WASHI }}>
                  {isSharing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
                </button>
                <button onClick={() => hasCompanion ? navigateTo("companion") : setShowUpgrade(true)}
                  className="inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                  style={{ ...monoBtn, padding: "10px 20px", borderRadius: 2, background: RED, border: "none", color: "#fff" }}
                  data-testid="start-trip-btn">
                  {hasCompanion ? <>Start Trip <ArrowRight className="w-4 h-4" /></> : <><Lock className="w-4 h-4" /> Start Trip</>}
                </button>
              </>}
            </div>
          </div>
        </div>

        {/* Share modal */}
        <AnimatePresence>
          {showShareModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }}>
              <motion.div ref={shareModalRef}
                initial={{ opacity: 0, y: -8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.95 }} transition={{ duration: 0.15 }}
                className="w-full max-w-sm p-5" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.3)" }} data-testid="share-modal">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold" style={{ color: "var(--zen-text)" }}>Share itinerary</span>
                  <button onClick={() => setShowShareModal(false)} className="p-0.5 hover:opacity-70" data-testid="share-modal-close"><X className="w-4 h-4" style={{ color: "var(--zen-sub)" }} /></button>
                </div>
                <p className="text-xs mb-3" style={{ color: "var(--zen-sub)" }}>Anyone with this link can view your trip as a beautiful page (PDF one tap away). Expires in 30 days.</p>
                <div className="flex items-center gap-2 mb-3">
                  <input readOnly value={shareLink} onClick={(e) => e.target.select()} className="jf-field" style={{ fontSize: 12, padding: "8px 10px" }} data-testid="share-link-input" />
                  <button onClick={copyToClipboard} className="shrink-0 inline-flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 2, border: "1px solid var(--zen-border-h)", background: "transparent", cursor: "pointer" }} data-testid="copy-share-link-btn">
                    {copied ? <Check className="w-4 h-4" style={{ color: "var(--morning)" }} /> : <Copy className="w-4 h-4" style={{ color: "var(--zen-text)" }} />}
                  </button>
                </div>
                {copied && <p className="text-xs mb-2" style={{ color: "var(--morning)" }}>Copied!</p>}
                <button onClick={shareWhatsApp} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-white hover:opacity-90" style={{ background: "#25D366", borderRadius: 2 }} data-testid="share-whatsapp-btn">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  Share via WhatsApp
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* ── BEFORE YOU GO — every booking, cash need and luggage day, auto-collected ── */}
        {(() => {
          const byg = beforeYouGo(itinerary);
          if (!byg.length && !isLocked) return null;
          const shown = byg.slice(0, 6);
          return (
            <div style={{ marginTop: 22, border: "1px solid var(--zen-border)", borderRadius: 4, padding: "16px 18px", background: "var(--zen-s1)" }} data-testid="before-you-go">
              <div className="flex items-center justify-between gap-3">
                <Eyebrow>Before you go</Eyebrow>
                {byg.length > shown.length && <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9, letterSpacing: ".12em", color: "var(--zen-dim)" }}>+{byg.length - shown.length} MORE IN YOUR PDF</span>}
              </div>
              <div style={{ marginTop: 10 }} className="space-y-2">
                {shown.map((x, i) => (
                  <div key={i} className="grid grid-cols-[56px_1fr]" style={{ gap: 10, alignItems: "baseline" }}>
                    <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", color: "var(--zen-dim)" }}>DAY {x.day}</span>
                    <span style={{ fontSize: 13.5, lineHeight: 1.4, color: "var(--zen-sub)" }}>{x.place && <strong style={{ fontWeight: 700, color: "var(--zen-text)" }}>{x.place} — </strong>}{tipText(x)}</span>
                  </div>
                ))}
                {isLocked && (
                  <p style={{ fontSize: 13, color: "var(--zen-dim)", marginTop: 6 }}>Bookings and cash notes for the locked days unfold with your full trip.</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── HOW WE BUILT YOUR PLAN — the rationale spread ── */}
        {(itinerary.trip_summary || itinerary.why_we_built_this) && (
          <div style={{ marginTop: 36 }}>
            <Eyebrow>How we built your plan</Eyebrow>
            {itinerary.trip_summary && (
              <p style={{ fontFamily: SANS, fontWeight: 500, fontSize: "clamp(15px, 1.8vw, 17px)", lineHeight: 1.62, color: "var(--zen-text)", marginTop: 12, textWrap: "pretty" }}>
                {itinerary.trip_summary}
              </p>
            )}
            {itinerary.why_we_built_this && (
              <div style={{ marginTop: 18, border: "1px solid var(--zen-border)", borderRadius: 4, padding: "18px 20px", background: "var(--zen-s2)" }}>
                <Eyebrow>Why we built this for you</Eyebrow>
                <p style={{ fontFamily: SANS, fontWeight: 500, fontSize: 14.5, lineHeight: 1.6, color: "var(--zen-sub)", marginTop: 10, textWrap: "pretty" }}>
                  {itinerary.why_we_built_this}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── DAYS ── */}
        <div style={{ marginTop: 36 }} className="space-y-10">
          {itinerary.days?.map((day, dayIndex) => {
            if (!day) return null;
            const dayNum = day.day_number ?? dayIndex + 1;
            const isFirstDay = dayNum === 1;
            const isLastDay = dayNum === totalDays;
            const openMaps = async () => {
              // the tab opens on the tap itself: Safari blocks one opened after waiting for the server
              const tab = window.open("about:blank", "_blank");
              try {
                const res = await axios.get(`${API}/itinerary/${itinerary.id}/maps-url?day_number=${dayNum}`);
                if (tab) tab.location.href = res.data.url; else window.location.href = res.data.url;
              } catch (e) {
                if (tab) tab.close();
                alert("We couldn't open the map for this day — try again in a moment.");
              }
            };

            // Teaser: the day exists — its content unfolds after purchase.
            if (day.locked) {
              return (
                <button key={dayNum} onClick={() => setShowUnlockModal(true)}
                  className="w-full text-left hover:opacity-90 transition-opacity"
                  style={{ background: "var(--zen-s1)", border: "1px dashed var(--zen-border-h)", borderRadius: 4, padding: "16px 20px", cursor: "pointer" }}
                  data-testid={`locked-day-${dayNum}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span style={{ background: RED, color: WASHI, borderRadius: 2, padding: "6px 10px", fontFamily: MONO, fontWeight: 600, fontSize: 10.5, letterSpacing: ".16em", whiteSpace: "nowrap" }}>
                          DAY {dayNum} · {(day.city_label || day.city || "").toUpperCase()}
                        </span>
                        <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 10, letterSpacing: ".16em", color: "var(--zen-dim)" }}>{fmtDocDate(day.date)}</span>
                      </div>
                      <h2 style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 17, letterSpacing: "-.01em", lineHeight: 1.2, color: "var(--zen-text)", marginTop: 10 }}>{day.title || `Day ${dayNum}`}</h2>
                      {day.description && <p className="truncate" style={{ fontSize: 13, color: "var(--zen-sub)", marginTop: 3 }}>{day.description}</p>}
                    </div>
                    <Lock className="w-5 h-5 shrink-0" style={{ color: "var(--zen-dim)" }} />
                  </div>
                </button>
              );
            }

            return (
              <div key={dayNum} data-testid={`day-section-${dayNum}`}>
                {/* Day header — chip, date, headline, lede */}
                <div style={{ borderBottom: "2px solid var(--zen-text)", paddingBottom: 12 }} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span style={{ background: RED, color: WASHI, borderRadius: 2, padding: "8px 12px", fontFamily: MONO, fontWeight: 600, fontSize: 11, letterSpacing: ".16em", whiteSpace: "nowrap" }}>
                      DAY {dayNum} · {(day.city_label || day.city || "").toUpperCase()}
                    </span>
                    <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 10.5, letterSpacing: ".16em", color: "var(--zen-dim)" }}>{fmtDocDate(day.date)}</span>
                  </div>
                  <button onClick={openMaps} title={`Open Day ${dayNum} route in Google Maps`} data-testid={`day-maps-btn-${dayNum}`}
                    className="inline-flex items-center justify-center shrink-0 hover:opacity-70" style={{ width: 34, height: 34, borderRadius: 2, border: "1px solid var(--zen-border-h)", background: "transparent", cursor: "pointer" }}>
                    <Map className="w-4 h-4" style={{ color: "var(--zen-text)" }} />
                  </button>
                </div>
                {day.title && (
                  <h2 style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: "clamp(19px, 2.6vw, 24px)", lineHeight: 1.12, letterSpacing: "-.01em", color: "var(--zen-text)", marginTop: 14, textWrap: "balance" }}>{day.title}</h2>
                )}
                {(day.description || day.day_description) && (
                  <p style={{ fontFamily: SANS, fontWeight: 500, fontSize: 14.5, lineHeight: 1.45, color: "var(--zen-sub)", marginTop: 8, maxWidth: "42em" }}>{day.description || day.day_description}</p>
                )}

                {/* Arrival (Day 1) */}
                {isFirstDay && arrivalTime && (
                  <div style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "10px 14px", marginTop: 14 }} data-testid="arrival-block">
                    <div className="flex items-center gap-2" style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-dim)" }}>
                      <Plane className="w-3.5 h-3.5" style={{ color: RED }} /> ARRIVAL — LANDS {fmtClock(arrivalTime)}{arrivalEvent?.end ? ` · ESTIMATED READY ${fmtClock(arrivalEvent.end)}` : ""}
                    </div>
                  </div>
                )}

                {/* Stop list */}
                <div style={{ marginTop: 14 }} className="space-y-2.5">
                  {(day.time_blocks || []).map((block, bi) => {
                    if (!block) return null;
                    const isTravel = block.time_of_day === "travel"
                      || block.cluster_name?.toLowerCase() === "travel"
                      || block.locations?.some(l => (l?.category || "").toLowerCase() === "transit" || (l?.category || "").toLowerCase() === "travel" || l?.name?.toLowerCase().startsWith("traveling:"));
                    const vibe = (block.vibe_line || "").trim();

                    return (
                      <div key={bi}>
                        {bi > 0 && <TransitBadge transit={day.time_blocks[bi - 1]?.transit_to_next} />}
                        {isTravel ? (
                          <TravelRow block={block} day={day} />
                        ) : (
                          <div>
                            {vibe && <p style={{ fontFamily: SANS, fontStyle: "italic", fontSize: 13, color: "var(--zen-dim)", margin: "2px 0 8px" }}>{vibe}</p>}
                            <div className="space-y-2.5">
                              {(block.locations || []).map((loc, li) => loc && (
                                <StopRow key={li} loc={loc} tod={block.time_of_day} dayCity={day.city} rowKey={`${dayNum}-${bi}-${li}`} />
                              ))}
                            </div>
                            {(block.pro_tip || block.avoid_tip) && (
                              <div style={{ marginTop: 8 }}>
                                {block.pro_tip && <p style={{ fontSize: 12.5, fontStyle: "italic", color: "var(--zen-sub)" }}>Pro tip: {block.pro_tip}</p>}
                                {block.avoid_tip && <p style={{ fontSize: 12.5, fontStyle: "italic", color: "var(--cat-nightlife-solid)" }}>Avoid: {block.avoid_tip}</p>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Departure (last day) */}
                {isLastDay && departureTime && (
                  <div style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "10px 14px", marginTop: 12 }} data-testid="departure-block">
                    <div className="flex items-center gap-2" style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--zen-dim)" }}>
                      <Plane className="w-3.5 h-3.5" style={{ color: RED, transform: "rotate(45deg)" }} /> DEPARTURE — {departureEvent?.start ? `LEAVE FOR THE AIRPORT ${fmtClock(departureEvent.start)} · ` : ""}FLIGHT {fmtClock(departureTime)}
                    </div>
                  </div>
                )}

                {/* TODAY'S NOTES — the document's pinned footer, per section */}
                {day.editorial_notes?.filter(Boolean).length > 0 && (
                  <div style={{ borderTop: "2px solid var(--zen-text)", marginTop: 16, paddingTop: 12 }}>
                    <Eyebrow>Today's notes</Eyebrow>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: "6px 24px", marginTop: 10 }}>
                      {day.editorial_notes.filter(Boolean).slice(0, 2).map((note, ni) => (
                        <p key={ni} style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--zen-sub)", margin: 0 }}>{note}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── JAPAN ESSENTIALS ── */}
        {itinerary.japan_essentials?.length > 0 && (
          <div style={{ marginTop: 44 }}>
            <Eyebrow>Japan essentials</Eyebrow>
            <h2 style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: "clamp(18px, 2.4vw, 22px)", lineHeight: 1.1, letterSpacing: "-.01em", color: "var(--zen-text)", marginTop: 12, maxWidth: "26em" }}>
              The things a 25-year local would insist you know.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 8, marginTop: 16 }}>
              {itinerary.japan_essentials.map((e, i) => (
                <div key={i} style={{ border: "1px solid var(--zen-border)", borderRadius: 4, padding: "10px 13px", background: "var(--zen-s1)" }}>
                  <h3 style={{ fontFamily: SANS, fontWeight: 800, fontSize: 15.5, color: "var(--zen-text)", margin: 0 }}>{e.title}</h3>
                  {e.tip && <p style={{ fontSize: 13.5, lineHeight: 1.45, color: "var(--zen-sub)", margin: "5px 0 0" }}>{e.tip}</p>}
                  {e.why && <p style={{ borderLeft: `2px solid ${RED}`, paddingLeft: 11, fontSize: 12.5, lineHeight: 1.42, color: "var(--zen-dim)", margin: "8px 0 0" }}>{e.why}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Honest notes */}
        {(() => {
          const cleanNotes = (itinerary.traveler_notes || []).filter(n => n && String(n).trim());
          if (cleanNotes.length === 0) return null;
          return (
            <div style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "14px 18px", marginTop: 20 }}>
              <Eyebrow color="var(--zen-sub)">A few honest notes</Eyebrow>
              <div style={{ marginTop: 8 }}>
                {cleanNotes.map((n, i) => (
                  <p key={i} className="text-xs leading-relaxed" style={{ color: "var(--zen-dim)" }}>• {n}</p>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Curator close — obsidian, like the document's last page */}
        <div style={{ background: INK, borderRadius: 4, padding: "18px 22px", marginTop: 24 }}>
          <Eyebrow>A note from your curator</Eyebrow>
          <p style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: "clamp(13.5px, 1.8vw, 15px)", lineHeight: 1.4, letterSpacing: "-.01em", color: WASHI, margin: "10px 0 0", maxWidth: "38em" }}>
            “Japan is a country that rewards slowness. The best moments happen when you stop trying to see everything and start letting the city breathe around you.”
          </p>
        </div>
      </div>

      {/* Teaser: persistent unfold bar */}
      {isLocked && (
        <div className="fixed bottom-0 left-0 right-0 z-40" style={{ background: "var(--zen-s1)", borderTop: "1px solid var(--zen-border)", boxShadow: "0 -6px 24px rgba(0,0,0,0.12)" }}>
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 px-5 py-3.5">
            <p className="text-sm min-w-0" style={{ color: "var(--zen-sub)" }}>
              <strong style={{ color: "var(--zen-text)" }}>Day 1 is yours to judge.</strong> The other {Math.max(0, (itinerary.days?.length || 1) - 1)} days are built and waiting.
            </p>
            <button onClick={() => setShowUnlockModal(true)}
              className="shrink-0 inline-flex items-center gap-1.5 hover:opacity-90"
              style={{ padding: "10px 18px", borderRadius: 2, background: RED, border: "none", color: "#fff", fontFamily: SANS, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
              data-testid="unlock-bar-btn">
              Unfold my trip <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <PaymentModal isOpen={showUnlockModal} onClose={() => setShowUnlockModal(false)}
        onCreditsReady={tryUnlockWithCredits} userEmail={userEmail} setUserEmail={setUserEmail} mode="unlock" />

      {actionError && (
        <div className="fixed left-1/2 -translate-x-1/2 z-[60] px-5 py-3 text-sm" role="alert"
          style={{ bottom: 24, background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 6, color: "var(--zen-text)", boxShadow: "var(--shadow-card-hover)", maxWidth: "90vw" }}>
          {actionError}
        </div>
      )}

      {showUpgrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm p-6" style={{ background: "var(--zen-bg)", border: "1px solid var(--zen-border)", borderRadius: 8 }} data-testid="upgrade-companion-modal">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" style={{ color: RED }} />
              <h3 style={{ fontFamily: SANS, fontWeight: 800, fontSize: 18, color: "var(--zen-text)" }}>Unlock Start Trip</h3>
            </div>
            <p className="text-sm mb-5" style={{ color: "var(--zen-sub)" }}>
              The mobile companion with on-the-go recalculation is included with the <strong style={{ color: RED }}>Unfolded</strong> plan.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowUpgrade(false)} className="flex-1 py-2.5 text-sm" style={{ border: "1px solid var(--zen-border)", borderRadius: 2, color: "var(--zen-sub)", background: "transparent", cursor: "pointer" }}>Maybe later</button>
              <button onClick={() => { setShowUpgrade(false); navigateTo("pricing"); }} className="zen-btn zen-btn-primary flex-1 py-2.5 text-sm" data-testid="upgrade-see-plans-btn">See plans</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ResultPage;
